<template>
  <v-dialog v-model="internalValue" max-width="1050px" scrollable @click:outside="closeModal">
    <v-card class="rounded-lg bg-white text-dark-primary">
      <!-- Loading Overlay -->
      <vue-element-loading :active="loading" />

      <!-- Modal Header -->
      <v-card-title class="pa-4 primary lighten-5 border-bottom d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-avatar color="primary" size="38" class="me-3">
            <v-icon color="white">mdi-bus-clock</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-black text-dark-primary">
              تفاصيل الرحلة #{{ plannedTrip ? plannedTrip.id : '' }}
              <span v-if="plannedTrip && plannedTrip.trip && plannedTrip.trip.route" class="text-subtitle-1 font-weight-bold text-dark-secondary me-2">
                — {{ plannedTrip.trip.route.name }}
              </span>
            </div>
            <div class="text-caption text-dark-secondary font-weight-medium">
              تاريخ التشغيل: {{ plannedTrip ? plannedTrip.planned_date : '—' }} | التوقيت المخطط: {{ formatPlannedTime }}
            </div>
          </div>
        </div>

        <v-btn icon color="grey darken-2" @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4 overflow-y-auto" style="max-height: 80vh;" v-if="plannedTrip">
        <!-- TOP KPI CARDS -->
        <v-row dense class="mb-3">
          <v-col cols="6" sm="4" md="2.4">
            <v-card outlined class="pa-2 text-center rounded-lg border-grey bg-light">
              <div class="text-caption text-dark-secondary font-weight-bold">إجمالي الحجوزات</div>
              <div class="text-h5 font-weight-black text-dark-primary mt-1">{{ kpis.total_reservations || 0 }}</div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="4" md="2.4">
            <v-card outlined class="pa-2 text-center rounded-lg border-success bg-light">
              <div class="text-caption text-dark-secondary font-weight-bold">الحجوزات النشطة</div>
              <div class="text-h5 font-weight-black green--text text--darken-3 mt-1">{{ kpis.active_reservations || 0 }}</div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="4" md="2.4">
            <v-card outlined class="pa-2 text-center rounded-lg border-info bg-light">
              <div class="text-caption text-dark-secondary font-weight-bold">صعد المركبة</div>
              <div class="text-h5 font-weight-black blue--text text--darken-3 mt-1">{{ kpis.boarded_count || 0 }}</div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="4" md="2.4">
            <v-card outlined class="pa-2 text-center rounded-lg border-warning bg-light">
              <div class="text-caption text-dark-secondary font-weight-bold">لم يحضر (No-show)</div>
              <div class="text-h5 font-weight-black warning--text text--darken-3 mt-1">{{ kpis.no_show_count || 0 }}</div>
            </v-card>
          </v-col>

          <v-col cols="6" sm="4" md="2.4">
            <v-card outlined class="pa-2 text-center rounded-lg border-danger bg-light">
              <div class="text-caption text-dark-secondary font-weight-bold">ملغاة</div>
              <div class="text-h5 font-weight-black red--text text--darken-3 mt-1">{{ kpis.cancelled_reservations || 0 }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- TABS NAV -->
        <v-tabs v-model="activeTab" class="border-bottom mb-4" color="primary" grow>
          <v-tab class="font-weight-bold">
            <v-icon left size="18">mdi-information-outline</v-icon>
            نظرة عامة
          </v-tab>
          <v-tab class="font-weight-bold">
            <v-icon left size="18">mdi-account-group-outline</v-icon>
            الركاب والحجوزات ({{ reservations.length }})
          </v-tab>
          <v-tab class="font-weight-bold">
            <v-icon left size="18">mdi-map-marker-path</v-icon>
            المحطات ({{ orderedStops.length }})
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- TAB 1: OVERVIEW -->
          <v-tab-item class="pt-2">
            <v-row dense>
              <!-- Captain Card -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-3 rounded-lg border-grey bg-white fill-height">
                  <div class="text-subtitle-2 font-weight-black primary--text mb-2">
                    <v-icon size="18" color="primary" class="me-1">mdi-card-account-details-outline</v-icon>
                    بيانات الكابتن (السائق التاريخي)
                  </div>
                  <div class="d-flex align-center">
                    <v-avatar size="50" color="grey lighten-3" class="me-3 border-grey">
                      <img v-if="plannedTrip.driver && plannedTrip.driver.avatar_url" :src="plannedTrip.driver.avatar_url" alt="Captain Avatar" />
                      <v-icon v-else size="32" color="grey darken-1">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-black text-subtitle-1 text-dark-primary">
                        {{ plannedTrip.driver ? plannedTrip.driver.name : 'سائق غير محدد' }}
                      </div>
                      <div class="text-body-2 text-dark-secondary font-weight-bold mt-1" dir="ltr" style="text-align: right;">
                        <v-icon size="16" color="grey">mdi-phone</v-icon>
                        {{ getDriverPhone(plannedTrip.driver) }}
                      </div>
                      <div v-if="plannedTrip.driver && plannedTrip.driver.driver_information" class="text-caption text-dark-secondary font-weight-medium mt-1">
                        رخصة السائق: {{ plannedTrip.driver.driver_information.license_number || '—' }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <!-- Vehicle Card -->
              <v-col cols="12" md="6">
                <v-card outlined class="pa-3 rounded-lg border-grey bg-white fill-height">
                  <div class="text-subtitle-2 font-weight-black primary--text mb-2">
                    <v-icon size="18" color="primary" class="me-1">mdi-bus</v-icon>
                    بيانات الحافلة / المركبة
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-caption text-dark-secondary font-weight-bold">رخصة / رقم اللوحة:</div>
                      <div class="text-subtitle-1 font-weight-black text-dark-primary">
                        {{ plannedTrip.bus ? plannedTrip.bus.license : (plannedTrip.driver && plannedTrip.driver.driver_information ? plannedTrip.driver.driver_information.vehicle_license_number : '—') }}
                      </div>
                    </div>
                    <div>
                      <div class="text-caption text-dark-secondary font-weight-bold">السعة الاستيعابية:</div>
                      <div class="text-subtitle-1 font-weight-black primary--text">
                        {{ plannedTrip.bus ? plannedTrip.bus.capacity + ' مقعد' : '—' }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <!-- Operational Timestamps Card -->
              <v-col cols="12" class="mt-2">
                <v-card outlined class="pa-3 rounded-lg border-grey bg-white">
                  <div class="text-subtitle-2 font-weight-black primary--text mb-2">
                    <v-icon size="18" color="primary" class="me-1">mdi-timeline-clock-outline</v-icon>
                    سجل المواعيد التشغيلية الفعلية
                  </div>
                  <v-row dense>
                    <v-col cols="12" sm="3">
                      <div class="text-caption text-dark-secondary font-weight-bold">تاريخ الرحلة المخطط:</div>
                      <div class="text-body-2 font-weight-bold text-dark-primary">{{ plannedTrip.planned_date }}</div>
                    </v-col>
                    <v-col cols="12" sm="3">
                      <div class="text-caption text-dark-secondary font-weight-bold">بدء التحرك الفعلي:</div>
                      <div class="text-body-2 font-weight-bold green--text text--darken-3">
                        {{ plannedTrip.started_at || plannedTrip.movement_started_at || 'لم تبدأ بعد' }}
                      </div>
                    </v-col>
                    <v-col cols="12" sm="3">
                      <div class="text-caption text-dark-secondary font-weight-bold">إنهاء الرحلة الفعلي:</div>
                      <div class="text-body-2 font-weight-bold blue--text text--darken-3">
                        {{ plannedTrip.ended_at || 'لم تنته بعد' }}
                      </div>
                    </v-col>
                    <v-col cols="12" sm="3">
                      <div class="text-caption text-dark-secondary font-weight-bold">حالة الرحلة:</div>
                      <v-chip x-small :color="getTripStatusColor(plannedTrip)" class="font-weight-bold white--text">
                        {{ getTripStatusText(plannedTrip) }}
                      </v-chip>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 2: PASSENGERS & BOOKINGS -->
          <v-tab-item class="pt-2">
            <v-data-table
              :headers="passengerHeaders"
              :items="reservations"
              dense
              outlined
              class="border-grey"
              :no-data-text="'لا توجد حجوزات مسجلة لهذه الرحلة'"
            >
              <template v-slot:item.ticket_number="{ item }">
                <span class="font-weight-bold primary--text">#{{ item.ticket_number || item.id }}</span>
              </template>

              <template v-slot:item.customer="{ item }">
                <div>
                  <div class="font-weight-bold text-dark-primary">{{ item.customer ? item.customer.name : 'عميل مجهول' }}</div>
                  <div class="text-caption text-dark-secondary font-weight-medium" dir="ltr" style="text-align: right;">
                    {{ getCustomerPhone(item.customer) }}
                  </div>
                </div>
              </template>

              <template v-slot:item.route_segment="{ item }">
                <div class="text-caption">
                  <div class="font-weight-bold text-dark-primary">
                    <v-icon size="12" color="green">mdi-circle-small</v-icon>
                    من: {{ item.first_stop ? item.first_stop.name : (item.start_address || '—') }}
                  </div>
                  <div class="font-weight-bold text-dark-primary mt-1">
                    <v-icon size="12" color="red">mdi-circle-small</v-icon>
                    إلى: {{ item.last_stop ? item.last_stop.name : (item.destination_address || '—') }}
                  </div>
                </div>
              </template>

              <template v-slot:item.seat_number="{ item }">
                <v-chip x-small outlined color="primary" class="font-weight-bold">
                  مقعد {{ item.seat_number || '—' }}
                </v-chip>
              </template>

              <template v-slot:item.paid_price="{ item }">
                <span class="font-weight-bold text-dark-primary">{{ item.paid_price || item.trip_price || 0 }} ج.م</span>
              </template>

              <template v-slot:item.status_badge="{ item }">
                <div class="d-flex flex-column gap-1">
                  <!-- Boarding / Ride Status Chip -->
                  <v-chip x-small :color="getReservationBadgeColor(item)" class="font-weight-bold white--text">
                    {{ getReservationBadgeText(item) }}
                  </v-chip>
                </div>
              </template>

              <template v-slot:item.cancellation_info="{ item }">
                <!-- 1. ADMIN CANCELLATION (VIA COMPLAINT) -->
                <div v-if="item.ride_status == 4 && item.complaint" class="text-caption">
                  <div class="font-weight-bold primary--text text--darken-2">المصدر: الإدارة (تسوية شكوى)</div>
                  <div class="text-dark-secondary font-weight-medium">السبب: {{ item.cancellation_reason || item.complaint.description || 'السبب غير مسجل' }}</div>
                </div>

                <!-- 2. CUSTOMER CANCELLATION (WITH REASON EVIDENCE) -->
                <div v-else-if="item.ride_status == 4 && item.cancellation_reason" class="text-caption">
                  <div class="font-weight-bold red--text text--darken-3">المصدر: العميل</div>
                  <div class="text-dark-secondary font-weight-medium">السبب: {{ item.cancellation_reason }}</div>
                </div>

                <!-- 3. AMBIGUOUS LEGACY CANCELLATION -->
                <div v-else-if="item.ride_status == 4" class="text-caption">
                  <div class="font-weight-bold grey--text text--darken-3">المصدر: غير محدد</div>
                  <div class="text-dark-secondary font-weight-medium">السبب: السبب غير مسجل</div>
                </div>

                <!-- 4. DRIVER NO-SHOW WITH EVIDENCE -->
                <div v-else-if="item.boarding_status === 'no_show' || item.no_show_by_driver_id" class="text-caption">
                  <div class="font-weight-bold warning--text text--darken-3">
                    المصدر: الكابتن {{ (item.no_show_by_driver && item.no_show_by_driver.name) ? '(' + item.no_show_by_driver.name + ')' : '' }}
                  </div>
                  <div class="text-dark-secondary font-weight-medium">
                    السبب: {{ item.no_show_reason_text || 'السبب غير مسجل' }}
                  </div>
                </div>

                <!-- 5. UNKNOWN / LEGACY MISSED RIDE -->
                <div v-else-if="item.ride_status == 2" class="text-caption">
                  <div class="font-weight-bold grey--text text--darken-3">المصدر: غير محدد</div>
                  <div class="text-dark-secondary font-weight-medium">السبب: السبب غير مسجل</div>
                </div>

                <div v-else class="text-caption text-dark-secondary font-weight-medium">
                  —
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- TAB 3: STOPS & ROUTE JOURNEY -->
          <v-tab-item class="pt-2">
            <v-data-table
              :headers="stopHeaders"
              :items="orderedStops"
              dense
              outlined
              class="border-grey"
              :no-data-text="'لا توجد محطات مسجلة لهذا المسار'"
            >
              <template v-slot:item.order="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-avatar size="28" color="primary" class="white--text font-weight-black elevation-1">
                    <span style="color: #ffffff !important; font-weight: 900 !important; font-size: 14px !important;">{{ item.order }}</span>
                  </v-avatar>
                </div>
              </template>

              <template v-slot:item.stop_name="{ item }">
                <span class="font-weight-black text-dark-primary text-subtitle-2">{{ item.stop_name }}</span>
              </template>

              <template v-slot:item.planned_timestamp="{ item }">
                <span class="text-dark-primary font-weight-bold text-caption">{{ item.planned_timestamp || '—' }}</span>
              </template>

              <template v-slot:item.visited="{ item }">
                <v-chip
                  x-small
                  :class="item.visited ? 'chip-visited' : 'chip-not-visited'"
                >
                  <v-icon x-small left v-if="item.visited" color="green darken-3">mdi-check-circle</v-icon>
                  {{ item.visited ? '✓ تم المرور (' + formatTimestamp(item.actual_timestamp) + ')' : '○ لم يتم المرور' }}
                </v-chip>
              </template>

              <template v-slot:item.boarding_count="{ item }">
                <v-chip x-small class="chip-boarding">
                  +{{ item.boarding_count }} صعود
                </v-chip>
              </template>

              <template v-slot:item.drop_count="{ item }">
                <v-chip x-small class="chip-drop-off">
                  -{{ item.drop_count }} نزول
                </v-chip>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions class="pa-3 grey lighten-4 border-top justify-end">
        <v-btn color="primary" outlined small class="font-weight-bold text-dark-primary" @click="closeModal">
          إغلاق النافذة
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VueElementLoading from "vue-element-loading";

export default {
  components: {
    VueElementLoading,
  },
  props: {
    value: Boolean,
    plannedTripId: [Number, String],
  },
  data() {
    return {
      loading: false,
      activeTab: 0,
      plannedTrip: null,
      orderedStops: [],
      kpis: {},
      passengerHeaders: [
        { text: "رقم التذكرة", value: "ticket_number" },
        { text: "الراكب", value: "customer" },
        { text: "المسار (محطة الصعود -> النزول)", value: "route_segment" },
        { text: "المقعد", value: "seat_number" },
        { text: "المبلغ المدفوع", value: "paid_price" },
        { text: "حالة الحجز", value: "status_badge" },
        { text: "تفاصيل الإلغاء / عدم الحضور", value: "cancellation_info" },
      ],
      stopHeaders: [
        { text: "الترتيب", value: "order" },
        { text: "اسم المحطة", value: "stop_name" },
        { text: "الموعد المخطط", value: "planned_timestamp" },
        { text: "حالة المرور الفعلي", value: "visited" },
        { text: "الركاب الصاعدون", value: "boarding_count" },
        { text: "الركاب النازلون", value: "drop_count" },
      ],
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    reservations() {
      return (this.plannedTrip && this.plannedTrip.reservations) ? this.plannedTrip.reservations : [];
    },
    formatPlannedTime() {
      if (!this.plannedTrip || !this.plannedTrip.planned_trip_detail) return "—";
      const details = this.plannedTrip.planned_trip_detail;
      if (details.length === 0) return "—";
      return details[0].planned_timestamp + ' → ' + details[details.length - 1].planned_timestamp;
    },
  },
  watch: {
    value(val) {
      if (val && this.plannedTripId) {
        this.fetchTripDetails();
      }
    },
    plannedTripId(newId) {
      if (this.value && newId) {
        this.fetchTripDetails();
      }
    },
  },
  methods: {
    fetchTripDetails() {
      this.loading = true;
      axios
        .get(`/planned-trips/${this.plannedTripId}/timetable-details`)
        .then((response) => {
          this.loading = false;
          this.plannedTrip = response.data.planned_trip;
          this.orderedStops = response.data.ordered_stops || [];
          this.kpis = response.data.kpis || {};
        })
        .catch((error) => {
          this.loading = false;
          console.error("Error fetching timetable trip details:", error);
          this.$notify({
            title: "خطأ",
            text: "تعذر تحميل تفاصيل الرحلة",
            type: "error",
          });
        });
    },
    closeModal() {
      this.internalValue = false;
    },
    getDriverPhone(driver) {
      if (!driver) return "—";
      return driver.tel_number || driver.phone_number || "—";
    },
    getCustomerPhone(customer) {
      if (!customer) return "—";
      return customer.tel_number || customer.phone_number || "—";
    },
    getTripStatusText(pt) {
      if (!pt) return "—";
      if (pt.ended_at) return "مكتملة";
      if (pt.started_at || pt.movement_started_at) return "جارية";
      if (pt.is_paused) return "متوقفة مؤقتًا";
      return "لم تبدأ بعد";
    },
    getTripStatusColor(pt) {
      if (!pt) return "grey";
      if (pt.ended_at) return "primary";
      if (pt.started_at || pt.movement_started_at) return "success";
      if (pt.is_paused) return "warning";
      return "info";
    },
    getReservationBadgeText(res) {
      if (res.ride_status == 4) return "ملغاة";
      if (res.boarding_status === 'no_show' || res.ride_status == 2 || res.no_show_by_driver_id) return "لم يحضر الراكب";
      if (res.boarding_status === 'boarded' || res.boarding_status === 'dropped_off' || res.ride_status == 1 || res.ride_status == 3) return "تم الصعود / مكتمل";
      return "قيد الانتظار";
    },
    getReservationBadgeColor(res) {
      if (res.ride_status == 4) return "red darken-2";
      if (res.boarding_status === 'no_show' || res.ride_status == 2 || res.no_show_by_driver_id) return "warning darken-2";
      if (res.boarding_status === 'boarded' || res.boarding_status === 'dropped_off' || res.ride_status == 1 || res.ride_status == 3) return "success darken-1";
      return "blue darken-1";
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      return String(ts).slice(-8);
    },
  },
};
</script>

<style scoped>
.text-dark-primary { color: #111827 !important; }
.text-dark-secondary { color: #374151 !important; }
.border-bottom { border-bottom: 1px solid #e5e7eb !important; }
.border-top { border-top: 1px solid #e5e7eb !important; }
.border-grey { border: 1px solid #e5e7eb !important; }
.border-success { border-top: 3px solid #2e7d32 !important; }
.border-info { border-top: 3px solid #0288d1 !important; }
.border-warning { border-top: 3px solid #ef6c00 !important; }
.border-danger { border-top: 3px solid #d32f2f !important; }
.bg-light { background-color: #f9fafb !important; }
.gap-1 { gap: 4px; }

/* Dedicated Light Badge Specific Classes */
.chip-not-visited {
  background-color: #f3f4f6 !important;
  border: 1px solid #d1d5db !important;
  color: #111827 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}
.chip-not-visited ::v-deep .v-chip__content {
  color: #111827 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.chip-visited {
  background-color: #f0fdf4 !important;
  border: 1px solid #bbf7d0 !important;
  color: #14532d !important;
  font-weight: 800 !important;
  opacity: 1 !important;
}
.chip-visited ::v-deep .v-chip__content {
  color: #14532d !important;
  font-weight: 800 !important;
  opacity: 1 !important;
}

.chip-boarding {
  background-color: #f0fdf4 !important;
  border: 1px solid #bbf7d0 !important;
  color: #14532d !important;
  font-weight: 800 !important;
  opacity: 1 !important;
}
.chip-boarding ::v-deep .v-chip__content {
  color: #14532d !important;
  font-weight: 800 !important;
  opacity: 1 !important;
}

.chip-drop-off {
  background-color: #eff6ff !important;
  border: 1px solid #bfdbfe !important;
  color: #1e3a8a !important;
  font-weight: 800 !important;
  opacity: 1 !important;
}
.chip-drop-off ::v-deep .v-chip__content {
  color: #1e3a8a !important;
  font-weight: 800 !important;
  opacity: 1 !important;
}

/* Ensure solid dark badges retain crisp white text */
::v-deep .v-chip.red,
::v-deep .v-chip.warning,
::v-deep .v-chip.success,
::v-deep .v-chip.primary,
::v-deep .v-chip.info {
  opacity: 1 !important;
}
::v-deep .v-chip.red .v-chip__content,
::v-deep .v-chip.warning .v-chip__content,
::v-deep .v-chip.success .v-chip__content,
::v-deep .v-chip.primary .v-chip__content,
::v-deep .v-chip.info .v-chip__content {
  color: #ffffff !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

::v-deep .v-tabs .v-tab {
  color: #374151 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}
::v-deep .v-tabs .v-tab--active {
  color: #6200ee !important;
  font-weight: 900 !important;
  opacity: 1 !important;
}

::v-deep .v-data-table {
  color: #111827 !important;
}
::v-deep .v-data-table thead th {
  color: #111827 !important;
  font-weight: 800 !important;
  font-size: 13px !important;
  background-color: #f8fafc !important;
}
::v-deep .v-data-table tbody td {
  color: #111827 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  opacity: 1 !important;
}
::v-deep .v-data-footer,
::v-deep .v-data-footer *,
::v-deep .v-pagination,
::v-deep .v-pagination * {
  color: #111827 !important;
  opacity: 1 !important;
}
</style>
